import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react'
import {
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
  CheckIcon,
  ClipboardDocumentIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline'
import { Button } from 'app/components/ui/button'
import { Input } from 'app/components/ui/input'
import { cn } from 'app/lib/utils'
import { logError } from 'lib/logging'
import { parseTranscript, segmentText, type SegmentKind, type TranscriptTurn } from './transcript'

const SEGMENT_CLASS: Record<SegmentKind, string> = {
  plain: ``,
  tone: `text-caption-sm italic text-minimal`,
  name: `rounded-sm bg-blue-100 px-0.5 font-medium text-blue-800`,
  family: `font-medium text-amber-700`,
  cue: `font-medium text-green-700`,
  match: `rounded-sm bg-amber-200 px-0.5 text-strong`,
}

const BUBBLE_CLASS: Record<TranscriptTurn[`side`], string> = {
  office: `rounded-2xl rounded-tl-md border border-default bg-surface-raised text-default`,
  caller: `rounded-2xl rounded-tr-md border border-blue-100 bg-blue-50 text-strong`,
  none: `text-default`,
}

const NO_NAMES: string[] = []

/**
 * A call transcript as a two-sided conversation, built for checking an answer key: the
 * names in the expected people list, family words ("son", "wife"…) and patient-type cues
 * ("new patient", "reschedule"…) are highlighted, since those decide who belongs in the
 * People stage and how each is typed. `names` should be memoized by the caller.
 */
export function TranscriptView({ text, names = NO_NAMES }: { text: string; names?: string[] }): ReactElement {
  const [query, setQuery] = useState(``)
  const [expanded, setExpanded] = useState(false)
  const [copied, setCopied] = useState(false)
  const listRef = useRef<HTMLOListElement>(null)

  const { turns, speakers } = useMemo(() => parseTranscript(text), [text])
  const segmented = useMemo(() => turns.map((turn) => segmentText(turn.text, { names, query })), [turns, names, query])
  const matchCount = segmented.flat().filter((segment) => segment.kind === `match`).length
  const hasNames = segmented.flat().some((segment) => segment.kind === `name`)
  const hasFamily = segmented.flat().some((segment) => segment.kind === `family`)
  const hasCue = segmented.flat().some((segment) => segment.kind === `cue`)
  // A speaker with no spoken turn at all (a silent caller) decides the answer key on its
  // own, and is easy to miss while scrolling a long one-sided transcript.
  const silentSpeakers = speakers.filter(
    (speaker) => !turns.some((turn) => turn.speaker === speaker.label && turn.silentCount === 0),
  )

  useEffect(() => {
    if (query.trim()) listRef.current?.querySelector(`[data-match]`)?.scrollIntoView({ block: `nearest` })
  }, [query])

  const copy = async () => {
    try {
      await window.navigator.clipboard.writeText(text)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1500)
    } catch (error) {
      logError(`Could not copy the transcript`, error)
    }
  }

  return (
    <section>
      <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-2">
        <h4 className="text-xs font-semibold uppercase text-subtle">Transcript</h4>
        {turns.length > 0 && (
          <span className="text-caption-sm text-minimal">
            {turns.length} {turns.length === 1 ? `turn` : `turns`}
            {speakers.length > 0 && ` · ${speakers.map((speaker) => speaker.label).join(` · `)}`}
          </span>
        )}
        <div className="flex w-full items-center gap-1.5 sm:ml-auto sm:w-auto">
          <Input
            size="sm"
            wrapperClassName="flex-1 sm:w-40 sm:flex-none"
            className="pl-8"
            icon={<MagnifyingGlassIcon className="h-3.5 w-3.5 text-minimal" />}
            placeholder="Find in transcript"
            aria-label="Find in transcript"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          <Button
            variant="tertiary"
            size="xs"
            aria-label={copied ? `Transcript copied` : `Copy transcript`}
            title={copied ? `Copied` : `Copy transcript`}
            onClick={copy}
          >
            {copied ? <CheckIcon className="h-4 w-4" /> : <ClipboardDocumentIcon className="h-4 w-4" />}
          </Button>
          <Button
            variant="tertiary"
            size="xs"
            aria-label={expanded ? `Collapse transcript` : `Expand transcript`}
            title={expanded ? `Collapse` : `Expand`}
            onClick={() => setExpanded((value) => !value)}
          >
            {expanded ? <ArrowsPointingInIcon className="h-4 w-4" /> : <ArrowsPointingOutIcon className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      {(hasNames || hasFamily || hasCue || query.trim()) && (
        <div className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-caption-sm text-subtle">
          {hasNames && (
            <span>
              <span className={SEGMENT_CLASS.name}>Name</span> in the answer key
            </span>
          )}
          {hasFamily && (
            <span>
              <span className={SEGMENT_CLASS.family}>son</span> family word
            </span>
          )}
          {hasCue && (
            <span>
              <span className={SEGMENT_CLASS.cue}>new patient</span> patient-type cue
            </span>
          )}
          {query.trim() && (
            <span>
              {matchCount} {matchCount === 1 ? `match` : `matches`}
            </span>
          )}
        </div>
      )}

      {silentSpeakers.length > 0 && (
        <p className="mb-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-1.5 text-caption-sm text-amber-800">
          {silentSpeakers.map((speaker) => speaker.label).join(` and `)} never{` `}
          {silentSpeakers.length === 1 ? `speaks` : `speak`} in this call.
        </p>
      )}

      {turns.length === 0 ? (
        <p className="rounded-lg border border-dashed border-default p-4 text-center text-caption-sm text-subtle">
          No transcript.
        </p>
      ) : (
        <ol
          ref={listRef}
          aria-label="Transcript"
          className={cn(
            `space-y-3 overflow-y-auto rounded-lg border border-default bg-surface-subtle p-3`,
            !expanded && `max-h-[28rem]`,
          )}
        >
          {turns.map((turn, index) => {
            const showSpeaker = turn.speaker !== null && turn.speaker !== turns[index - 1]?.speaker
            return (
              <li
                // Turns never reorder; the index is a stable key within one transcript.
                // oxlint-disable-next-line react/no-array-index-key
                key={index}
                className={cn(`flex flex-col`, turn.side === `caller` ? `items-end` : `items-start`)}
              >
                {showSpeaker && (
                  <div className="mb-1 flex items-baseline gap-1.5 px-1 text-caption-sm">
                    <span className="font-medium text-subtle">{turn.speaker}</span>
                    {turn.time && <span className="tabular-nums text-minimal">{turn.time}</span>}
                  </div>
                )}
                {turn.silentCount > 0 ? (
                  <span className="rounded-full border border-dashed border-default px-2.5 py-0.5 text-caption-sm text-minimal">
                    No response{turn.silentCount > 1 && ` ×${turn.silentCount}`}
                  </span>
                ) : (
                  <p
                    className={cn(
                      `text-sm leading-relaxed`,
                      turn.side !== `none` && `max-w-[85%] px-3 py-2`,
                      BUBBLE_CLASS[turn.side],
                    )}
                  >
                    {segmented[index].map((segment, part) =>
                      segment.kind === `plain` ? (
                        segment.text
                      ) : (
                        <span
                          // Segments are derived from the text; order is stable.
                          // oxlint-disable-next-line react/no-array-index-key
                          key={part}
                          data-match={segment.kind === `match` ? `` : undefined}
                          className={SEGMENT_CLASS[segment.kind]}
                        >
                          {segment.text}
                        </span>
                      ),
                    )}
                  </p>
                )}
              </li>
            )
          })}
        </ol>
      )}
    </section>
  )
}
